
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import {LaunchDialogService} from '@spartacus/storefront';

import {AimoRoutingService} from '../../../cms-structure/routing/aimo-routing.service';
import {AimoCart, AimoDayGroupedEntries, AimoOrderEntry} from '../../../model/cart.model';
import {AimoGTMProductAttributes, GTMCartType, GTMItemListId} from '../../../service/gtm/aimo-gtm.model';
import {AimoGtmService} from '../../../service/gtm/aimo-gtm.service';
import {EXCEL_IMPORT} from '../../cart-details/aimo-cart-layout.config';
import {
    AimoOrderTemplateDialogData,
    ORDER_TEMPLATE
} from '../../order-template/order-template-modal/aimo-order-template-layout.config';
import {hideDiv} from '../../shared/utils/spinner/aimo-spinner-utils';

@Component({
    selector: 'aimo-cart-item-list',
    templateUrl: './aimo-cart-item-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AimoCartItemListComponent implements AfterViewInit {
    @Input()
    cart: AimoCart;

    @Input()
    dayGroupedEntries: AimoDayGroupedEntries;

    @Input()
    alreadyOrderedEntriesOpen = {};

    @Output() toggleList = new EventEmitter<boolean>();
    @Output() toggleAlreadyOrderedList = new EventEmitter<boolean>();

    @Input() showHideList = false;
    @Input() showHideAlreadyOrderedList = false;

    @Input() paymentErrorStatus: string;

    @ViewChild('mobileAlreadyOrdered') mobileAlreadyOrdered: ElementRef<HTMLElement>;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        protected aimoRoutingService: AimoRoutingService,
        protected launchDialogService: LaunchDialogService,
        protected gtmService: AimoGtmService,
    ) {
    }

    ngAfterViewInit(): void {
        // Toggle already ordered orders open for mobile if we have openOrders url parameter
        if (!this.showHideAlreadyOrderedList && this.mobileAlreadyOrdered?.nativeElement != null) {
            setTimeout(() => {
                if (Object.keys(this.alreadyOrderedEntriesOpen).length != 0) {
                    this.toggleMobileAlreadyOrderedList();
                }
            }, 100);
        }
    }

    toggleMobileList(): void {
        this.toggleList.emit(!this.showHideList);
    }

    toggleMobileAlreadyOrderedList(): void {
        this.toggleAlreadyOrderedList.emit(!this.showHideAlreadyOrderedList);
    }

    isOpen(orderNumber: string): boolean {
        return this.alreadyOrderedEntriesOpen[orderNumber] === true;
    }

    toggleAlreadyOrderedEntries(orderNumber: string): void {
        this.alreadyOrderedEntriesOpen[orderNumber] = !this.isOpen(orderNumber);
    }

    toggleMobileListAndScrollTo(id: string): void {
        if (!this.showHideList) {
            this.toggleMobileList();
            // wait a moment to get elements visible for scroll
            setTimeout(() => {
                this.scrollTo(id);
            }, 100);
        } else {
            this.scrollTo(id);
        }
    }

    scrollTo(id: string): void {
        const productElement = this.document.querySelector('#' + id);
        if (productElement) {
            productElement.scrollIntoView({block: 'nearest'});
        } else {
            this.document.querySelector('aimo-cart-item-list').scrollIntoView({block: 'nearest'});
        }
    }

    hideMessage(div: HTMLElement): void {
        hideDiv(div);
    }

    public openExcelImportModal(): void {
        this.launchDialogService.closeDialog(null);
        this.launchDialogService.openDialogAndSubscribe(EXCEL_IMPORT, undefined);
    }

    importFromOrderTemplate(sourceId: string): void {
        this.launchDialogService.closeDialog(null);
        this.launchDialogService.openDialogAndSubscribe(ORDER_TEMPLATE, undefined, {
            importFrom: true,
            focusIdAfterClose: sourceId,
        } as AimoOrderTemplateDialogData);
    }

    gtmAttributes: AimoGTMProductAttributes;

    createGtmAttributes(idx: number, entry: AimoOrderEntry): AimoGTMProductAttributes {
        if (this.gtmAttributes === undefined) {
            this.gtmAttributes = {
                ...this.gtmService.createProductGtmAttributesBase(entry.product, GTMItemListId.cart_items),
                gtmParams: {
                    ...entry.gtmParams,
                    gtmItemListId: entry.gtmParams?.gtmItemListId ?? GTMItemListId.cart_items
                },
                index: idx,
                cart_type: GTMCartType.cart,
            } as AimoGTMProductAttributes;
        }
        return this.gtmAttributes;
    }

    isPikatukku(): boolean {
        return this.aimoRoutingService.isPikatukku();
    }

    refreshPage(): void {
        this.document.location.reload();
    }
}
